import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';

const highlights = [
    {
        title: 'Nestled in Nature',
        description: 'Located in the serene village of Kookal, 35 km from Kodaikanal town, surrounded by ancient Shola forests, rolling grasslands, and mist-covered hills.',
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
        ),
    },
    {
        title: 'Warm Hospitality',
        description: 'Our family-run homestay ensures every guest feels at home. From personalized attention to local insights, we go the extra mile to make your stay special.',
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
        ),
    },
    {
        title: 'Kookal Lake Access',
        description: 'Just a short walk from our property, Kookal Lake is a hidden paradise. Visit during sunrise for a magical experience with mist rising off the water.',
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            </svg>
        ),
    },
    {
        title: 'Sustainable Living',
        description: 'We are committed to eco-friendly practices — from solar heating to locally sourced organic meals. Nature gives us so much, and we give back with care.',
        icon: (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 3.03v.568c0 .334.148.65.405.864l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 01-1.161.886l-.143.048a1.107 1.107 0 00-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 01-1.652.928l-.679-.906a1.125 1.125 0 00-1.906.172L4.5 15.75l-.612.153M12.75 3.031a9 9 0 00-8.862 12.872M12.75 3.031a9 9 0 016.69 14.036m0 0l-.177-.529A2.25 2.25 0 0017.128 15H16.5l-.324-.324a1.453 1.453 0 00-2.328.377l-.036.073a1.586 1.586 0 01-.982.816l-.99.282c-.55.157-.894.702-.8 1.267l.073.438c.08.474.49.821.97.821.846 0 1.598.542 1.865 1.345l.215.643m5.276-3.67a9.012 9.012 0 01-5.276 3.67" />
            </svg>
        ),
    },
];

const timeline = [
    { year: 'The Dream', text: 'What started as a family dream to share the beauty of Kookal with travelers from around the world.' },
    { year: 'The Build', text: 'Carefully constructed using locally sourced materials, blending seamlessly with the natural surroundings.' },
    { year: 'The Welcome', text: 'UC Eden Valley opened its doors, welcoming the first guests to experience the magic of Kookal.' },
    { year: 'The Future', text: 'Continuously evolving to offer better experiences while preserving the pristine beauty of our surroundings.' },
];

export default function AboutPage() {
    return (
        <main>
            {/* Hero */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <img src="/images/surroundings/valley.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
                <div className="relative z-10 container-custom text-center px-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
                        <span className="text-white/80 font-sans text-xs tracking-widest uppercase">Our Story</span>
                    </div>
                    <h1 className="heading-display text-white mb-4">
                        About <span className="italic text-gold-300">Eden Valley</span>
                    </h1>
                    <p className="font-body text-xl text-white/60 max-w-xl mx-auto">
                        A story of passion, nature, and the desire to create unforgettable experiences in the hills of Kodaikanal.
                    </p>
                </div>
            </section>

            {/* Story */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <AnimatedSection>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-px bg-gold-500" />
                                <span className="font-sans text-xs font-semibold tracking-widest uppercase text-gold-600 dark:text-gold-400">
                                    Our Beginning
                                </span>
                            </div>
                            <h2 className="heading-section text-gray-900 dark:text-white mb-6">
                                Born from a <span className="italic text-gold-600 dark:text-gold-400">Love</span> for the Hills
                            </h2>
                            <div className="space-y-4 font-sans text-gray-600 dark:text-gray-300 leading-relaxed">
                                <p>
                                    UC Eden Valley was born from a simple yet profound love for the hills of Kodaikanal.
                                    What began as a family retreat in the quiet village of Kookal has transformed into a
                                    haven for travelers seeking peace, beauty, and authentic experiences.
                                </p>
                                <p>
                                    Kookal is not your typical tourist destination — it's a hidden gem. Away from the
                                    bustling town center, it offers a raw, untouched version of Kodaikanal that few
                                    get to experience. The village sits at an elevation that offers sweeping views of
                                    the valley below, and the famous Kookal Lake is just a gentle walk from our doorstep.
                                </p>
                                <p>
                                    We built Eden Valley with the philosophy that luxury and nature can coexist beautifully.
                                    Every brick, every beam, and every corner of this property has been designed to honor
                                    the natural surroundings while providing our guests with comfort they deserve.
                                </p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={0.2}>
                            <div className="relative">
                                <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                                    <img
                                        src="/images/surroundings/front.webp"
                                        alt="UC Eden Valley property front view"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute -bottom-4 -left-4 w-full h-full rounded-2xl border-2 border-gold-500/20 -z-10" />
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* Highlights */}
            <section className="section-padding bg-gray-50 dark:bg-royal-600/20">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-16">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <div className="w-8 h-px bg-gold-500" />
                            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-gold-600 dark:text-gold-400">
                                Why Choose Us
                            </span>
                            <div className="w-8 h-px bg-gold-500" />
                        </div>
                        <h2 className="heading-section text-gray-900 dark:text-white">
                            What Makes Us <span className="italic text-gold-600 dark:text-gold-400">Special</span>
                        </h2>
                    </AnimatedSection>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                        {highlights.map((item, index) => (
                            <AnimatedSection key={index} delay={index * 0.1}>
                                <div className="glass-card p-8 h-full flex gap-5 group hover:-translate-y-1 hover:shadow-xl hover:shadow-gold-500/5 transition-all duration-500">
                                    <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-gold-50 dark:bg-gold-500/10 flex items-center justify-center text-gold-600 dark:text-gold-400 group-hover:bg-gold-500 group-hover:text-white transition-all duration-500">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                            {item.title}
                                        </h3>
                                        <p className="font-sans text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Journey Timeline */}
            <section className="section-padding">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-16">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <div className="w-8 h-px bg-gold-500" />
                            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-gold-600 dark:text-gold-400">
                                Our Journey
                            </span>
                            <div className="w-8 h-px bg-gold-500" />
                        </div>
                        <h2 className="heading-section text-gray-900 dark:text-white">
                            The Eden Valley <span className="italic text-gold-600 dark:text-gold-400">Story</span>
                        </h2>
                    </AnimatedSection>

                    <div className="max-w-2xl mx-auto">
                        {timeline.map((item, index) => (
                            <AnimatedSection key={index} delay={index * 0.15}>
                                <div className="flex gap-6 mb-10 last:mb-0">
                                    {/* Timeline dot and line */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-4 h-4 rounded-full bg-gold-500 border-4 border-gold-100 dark:border-royal-500 flex-shrink-0 mt-1" />
                                        {index < timeline.length - 1 && (
                                            <div className="w-px flex-grow bg-gold-200 dark:bg-gold-500/20 mt-2" />
                                        )}
                                    </div>
                                    {/* Content */}
                                    <div className="pb-6">
                                        <h3 className="font-display text-lg font-bold text-gold-600 dark:text-gold-400 mb-2">
                                            {item.year}
                                        </h3>
                                        <p className="font-sans text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {item.text}
                                        </p>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Location */}
            <section className="section-padding bg-gray-50 dark:bg-royal-600/20">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-12">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <div className="w-8 h-px bg-gold-500" />
                            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-gold-600 dark:text-gold-400">
                                Find Us
                            </span>
                            <div className="w-8 h-px bg-gold-500" />
                        </div>
                        <h2 className="heading-section text-gray-900 dark:text-white mb-4">
                            How to <span className="italic text-gold-600 dark:text-gold-400">Reach</span> Us
                        </h2>
                        <p className="text-body-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                            UC Eden Valley is located in Kookal Village, approximately 35 km from Kodaikanal town center.
                        </p>
                    </AnimatedSection>

                    <AnimatedSection>
                        <div className="glass-card overflow-hidden">
                            <div className="aspect-[21/9] bg-[#07250A] flex items-center justify-center">
                                <div className="text-center p-8">
                                    <svg className="w-12 h-12 mx-auto text-forest-400 dark:text-gold-500/30 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
                                    </svg>
                                    <p className="font-sans text-sm text-forest-600 dark:text-gray-400">
                                        Embed Google Maps iframe here for Kookal, Kodaikanal
                                    </p>
                                    <a
                                        href="https://maps.google.com/?q=Kookal,Kodaikanal"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 mt-4 text-gold-600 dark:text-gold-400 font-sans text-sm hover:underline"
                                    >
                                        Open in Google Maps →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>

                    <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                        {[
                            { label: 'From Kodaikanal', value: '~35 km', sub: 'Scenic hill drive, ~1.5 hrs' },
                            { label: 'From Madurai Airport', value: '~140 km', sub: 'Via Kodaikanal road, ~4 hrs' },
                            { label: 'From Palani', value: '~80 km', sub: 'Via Kodaikanal-Palani road, ~3 hrs' },
                        ].map((item, i) => (
                            <div key={i} className="glass-card p-6 text-center">
                                <p className="font-sans text-xs tracking-wider uppercase text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
                                <p className="font-display text-2xl font-bold text-gold-600 dark:text-gold-400">{item.value}</p>
                                <p className="font-sans text-xs text-gray-500 dark:text-gray-400 mt-1">{item.sub}</p>
                            </div>
                        ))}
                    </AnimatedSection>
                </div>
            </section>

            {/* CTA */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[#07250A]" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent" />
                <div className="relative z-10 section-padding text-center">
                    <AnimatedSection>
                        <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
                            Come, <span className="italic text-gold-300">Experience</span> Eden Valley
                        </h2>
                        <p className="font-body text-xl text-white/70 max-w-xl mx-auto mb-10">
                            We'd love to welcome you to our little piece of paradise in the hills.
                        </p>
                        <Link to="/contact" className="btn-primary text-base px-10">
                            Plan Your Visit →
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </main>
    );
}
